import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { BookOpen, GraduationCap, Clock, CalendarDays, Flame, Trophy } from "lucide-react";
import api from "@/config/api";
import { StatCard } from "@/components/StatCard";
import { Loading, EmptyState } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";

export default function Progress() {
  useSeo("Progression", "Suis ta progression dans la mémorisation du Coran : versets appris et maîtrisés, temps d'étude, séances, séries et progression par sourate.");
  const { data: summary, isLoading } = useQuery({ queryKey: ["progress-summary"], queryFn: async () => (await api.get("/progress/summary")).data });
  const { data: weekly } = useQuery({ queryKey: ["weekly"], queryFn: async () => (await api.get("/sessions/weekly")).data });

  if (isLoading) return <Loading full />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Progression</h1>
        <p className="text-slate-500">Suis ton avancée dans la mémorisation du Coran.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={BookOpen} label="Versets appris" value={summary.verses_learned} tint="primary" />
        <StatCard icon={GraduationCap} label="Versets maîtrisés" value={summary.verses_mastered} tint="success" />
        <StatCard icon={Clock} label="Temps total" value={`${summary.total_minutes} min`} tint="secondary" />
        <StatCard icon={CalendarDays} label="Séances" value={summary.sessions_count} tint="primary" />
        <StatCard icon={Flame} label="Série actuelle" value={`${summary.streak} j`} tint="accent" />
        <StatCard icon={Trophy} label="Meilleure série" value={`${summary.best_streak} j`} tint="accent" />
      </div>

      {/* Weekly chart */}
      <section className="bg-white p-6 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
        <h2 className="font-black text-slate-800 mb-4">Cette semaine (minutes)</h2>
        <div className="h-56" data-testid="weekly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weekly || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 12, fontWeight: 700, fill: "#94a3b8" }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} width={28} />
              <Tooltip cursor={{ fill: "rgba(107,157,242,0.08)" }} contentStyle={{ borderRadius: 16, border: "1px solid #e2e8f0", fontWeight: 700 }} />
              <Bar dataKey="minutes" fill="hsl(217 84% 68%)" radius={[8, 8, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Per surah */}
      <section>
        <h2 className="font-black text-slate-800 mb-4">Progression par sourate</h2>
        {!summary.per_surah?.length ? (
          <EmptyState icon={BookOpen} title="Aucune sourate commencée" subtitle="Apprends ton premier verset pour voir ta progression ici." />
        ) : (
          <div className="space-y-3">
            {summary.per_surah.map((s) => {
              const pct = s.ayah_count ? Math.round((s.learned / s.ayah_count) * 100) : 0;
              return (
                <div key={s.surah_number} className="bg-white p-4 rounded-2xl border border-slate-100" data-testid={`surah-progress-${s.surah_number}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-extrabold text-slate-700 text-sm">{s.name_fr}</span>
                    <span className="text-xs font-bold text-slate-400">{s.learned}/{s.ayah_count} versets · {s.mastered} maîtrisés</span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-[width]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
